import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Table } from '@heroui/react';
import type { CatalogImportListItem } from '@/api/models';
import { countParam } from '@/pages/catalogImport/notices';
import { cn } from '@/utils/cnUtils';
import { formatInLanguage } from '@/utils/dateLocale';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { isUndoneImport, type DoneImportFigures } from '../../doneImports';

/** When the import ran, in the display zone (as Result prints it). */
const IMPORTED_AT_FORMAT = 'DD/MM/YYYY HH:mm';

const NUMBER_CLASS = 'block text-end tabular-nums';

type FiguresLineProps = {
    figures: Pick<DoneImportFigures, 'rows' | 'products' | 'units'>;
    /** What precedes the figures on the line, e.g. the date. */
    lead?: string;
};

/** The figures the narrow table folds under the file: rows, products, units. */
const FiguresLine: React.FC<FiguresLineProps> = ({ figures, lead }) => {
    const { t } = useTranslation('pages');
    const line = t('onboarding.done.imports.figures', {
        rows: countParam(figures.rows),
        products: countParam(figures.products),
        units: countParam(figures.units),
    });

    return (
        <span className="block text-xs font-normal text-muted">
            {lead ? `${lead} · ${line}` : line}
        </span>
    );
};

type ImportRowProps = {
    item: CatalogImportListItem;
    isNarrow: boolean;
};

/**
 * One import: the file (with an "Undone" chip when undone), then the date and
 * figures in their columns, or folded under the file on a phone. An undone
 * import is muted throughout.
 */
export const ImportRow: React.FC<ImportRowProps> = ({ item, isNarrow }) => {
    const { t, i18n } = useTranslation('pages');
    const isUndone = isUndoneImport(item);
    const importedAt = formatInLanguage(
        item.createdAt,
        IMPORTED_AT_FORMAT,
        i18n.language,
    );
    const figures = {
        rows: item.rowCount,
        products: item.productsCreated,
        units: item.units,
    };
    const fileName = item.fileName ?? t('onboarding.done.imports.unnamedFile');
    const tone = isUndone ? 'text-muted' : undefined;

    return (
        <Table.Row id={item.id}>
            <Table.Cell>
                <span
                    className={cn('flex min-w-0 flex-col gap-0.5', tone)}
                    data-status={item.status}
                    {...getNodeTestIdProp(`onboarding-done-import-${item.id}`)}
                >
                    {/* The whole name, wrapped where it must (file names rarely
                        have spaces); the chip moves under it when short of room. */}
                    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="min-w-0 break-all">{fileName}</span>
                        {isUndone ? (
                            <Chip
                                className="shrink-0"
                                size="sm"
                                variant="soft"
                                {...getNodeTestIdProp(
                                    `onboarding-done-import-undone-${item.id}`,
                                )}
                            >
                                {t('onboarding.done.imports.undone')}
                            </Chip>
                        ) : null}
                    </span>
                    {isNarrow ? (
                        <FiguresLine figures={figures} lead={importedAt} />
                    ) : null}
                </span>
            </Table.Cell>
            {isNarrow ? null : (
                <Table.Cell>
                    <span
                        className={cn('whitespace-nowrap tabular-nums', tone)}
                    >
                        {importedAt}
                    </span>
                </Table.Cell>
            )}
            {isNarrow ? null : (
                <Table.Cell>
                    <span className={cn(NUMBER_CLASS, tone)}>
                        {countParam(item.rowCount)}
                    </span>
                </Table.Cell>
            )}
            {isNarrow ? null : (
                <Table.Cell>
                    <span className={cn(NUMBER_CLASS, tone)}>
                        {countParam(item.productsCreated)}
                    </span>
                </Table.Cell>
            )}
            {isNarrow ? null : (
                <Table.Cell>
                    <span className={cn(NUMBER_CLASS, tone)}>
                        {countParam(item.units)}
                    </span>
                </Table.Cell>
            )}
            <Table.Cell>
                <span className={cn(NUMBER_CLASS, tone)}>
                    {formatGel(item.netCostTetri, { withSymbol: false })}
                </span>
            </Table.Cell>
        </Table.Row>
    );
};

type SubtotalRowProps = {
    branchId: string;
    figures: DoneImportFigures;
    isNarrow: boolean;
};

/** The branch's total of its standing imports, in semibold. */
export const SubtotalRow: React.FC<SubtotalRowProps> = ({
    branchId,
    figures,
    isNarrow,
}) => {
    const { t } = useTranslation('pages');
    const testId = `onboarding-done-subtotal-${branchId}`;

    return (
        <Table.Row id={testId}>
            <Table.Cell>
                <span
                    className="flex flex-col gap-0.5 font-semibold"
                    {...getNodeTestIdProp(testId)}
                >
                    {t('onboarding.done.imports.subtotal')}
                    {isNarrow ? <FiguresLine figures={figures} /> : null}
                </span>
            </Table.Cell>
            {isNarrow ? null : <Table.Cell>{''}</Table.Cell>}
            {isNarrow ? null : (
                <Table.Cell>
                    <span className={cn(NUMBER_CLASS, 'font-semibold')}>
                        {countParam(figures.rows)}
                    </span>
                </Table.Cell>
            )}
            {isNarrow ? null : (
                <Table.Cell>
                    <span className={cn(NUMBER_CLASS, 'font-semibold')}>
                        {countParam(figures.products)}
                    </span>
                </Table.Cell>
            )}
            {isNarrow ? null : (
                <Table.Cell>
                    <span className={cn(NUMBER_CLASS, 'font-semibold')}>
                        {countParam(figures.units)}
                    </span>
                </Table.Cell>
            )}
            <Table.Cell>
                <span
                    className={cn(NUMBER_CLASS, 'font-semibold')}
                    {...getNodeTestIdProp(`${testId}-value`)}
                >
                    {formatGel(figures.stockValueTetri, { withSymbol: false })}
                </span>
            </Table.Cell>
        </Table.Row>
    );
};
